// src/hooks/useResponsiveLayout.ts
import { useEffect, useState } from "react";
var CANVAS_LG_BREAKPOINT = 1200;
var CANVAS_MD_BREAKPOINT = 768;
var getCanvasWindowSize = () => {
  const { innerWidth: width } = window;
  if (width >= CANVAS_LG_BREAKPOINT) {
    return "lg";
  } else if (width >= CANVAS_MD_BREAKPOINT) {
    return "md";
  } else {
    return "sm";
  }
};
var getCustomBreakPoint = (customBreakPoints) => {
  const { innerWidth: width } = window;
  const sortedBreakPoints = Object.entries(customBreakPoints).sort(
    (a, b) => b[1] - a[1]
  );
  for (let [key, value] of sortedBreakPoints) {
    if (width >= value) {
      return key;
    }
  }
  return null;
};
var useResponsiveLayout = (customBreakPoints = {}) => {
  const [canvasScreenSize, setCanvasScreenSize] = useState(
    getCanvasWindowSize()
  );
  const [customBreakPoint, setCustomBreakPoint] = useState(
    getCustomBreakPoint(customBreakPoints)
  );
  useEffect(() => {
    const handleAdjustPageSize = () => {
      setCanvasScreenSize(getCanvasWindowSize());
      setCustomBreakPoint(getCustomBreakPoint(customBreakPoints));
    };
    window.addEventListener("resize", handleAdjustPageSize);
    return () => {
      window.removeEventListener("resize", handleAdjustPageSize);
    };
  }, [JSON.stringify(customBreakPoints)]);
  return { canvasScreenSize, customBreakPoint };
};
var useResponsiveLayout_default = useResponsiveLayout;

// src/components/IsolatedComponent.tsx
import React, { useCallback } from "react";
import { jsx } from "react/jsx-runtime";
var IsolatedComponent = ({
  as: As = "span",
  ...otherProps
}) => {
  const stopParentComponentEvents = useCallback((event) => {
    event.stopPropagation();
  }, []);
  return /* @__PURE__ */ jsx(
    As,
    {
      onClick: stopParentComponentEvents,
      onKeyDown: stopParentComponentEvents,
      onPointerDown: stopParentComponentEvents,
      ...otherProps
    }
  );
};
IsolatedComponent.displayName = "IsolatedComponent";
var IsolatedComponent_default = React.memo(IsolatedComponent);

// src/hooks/useGraphQL.ts
import { useState as useState2, useEffect as useEffect2, useCallback as useCallback2, useRef } from "react";
import { v4 as uuid } from "uuid";
var queryCache = /* @__PURE__ */ new Map();
var CACHE_TTL = 5 * 60 * 1e3;
function getCacheKey(query, variables) {
  return JSON.stringify({ query, variables });
}
function getFromCache(key) {
  const cached = queryCache.get(key);
  if (!cached) return null;
  const isExpired = Date.now() - cached.timestamp > CACHE_TTL;
  if (isExpired) {
    queryCache.delete(key);
    return null;
  }
  return cached.data;
}
function setCache(key, data) {
  queryCache.set(key, { data, timestamp: Date.now() });
}
var getDefaultHeaders = () => {
  const headers = {
    "Content-Type": "application/json"
  };
  const tokenString = localStorage.getItem("userLoginState");
  try {
    const tokenAsJSON = tokenString ? JSON.parse(tokenString) : null;
    if (tokenAsJSON) {
      headers["X-AUTH-TOKEN"] = tokenAsJSON.userProfileToken || "";
      headers["X-Api-Id"] = uuid();
      headers["Authorization"] = tokenAsJSON.accessToken ? `Bearer ${tokenAsJSON.accessToken}` : "";
    }
  } catch (error) {
    console.error("Error parsing userLoginState from localStorage", error);
    return { "Content-Type": "application/json" };
  }
  return headers;
};
function useGraphQL(query, options = {}) {
  const {
    variables,
    skip = false,
    fetchPolicy = "cache-and-network",
    headers
  } = options;
  const [data, setData] = useState2(void 0);
  const [loading, setLoading] = useState2(!skip);
  const [error, setError] = useState2(void 0);
  const [networkStatus, setNetworkStatus] = useState2(1);
  const abortControllerRef = useRef(null);
  const variablesRef = useRef(variables);
  const headersRef = useRef(headers);
  useEffect2(() => {
    variablesRef.current = variables;
    headersRef.current = headers;
  });
  const executeQuery = useCallback2(
    async (queryVariables, bypassCache = false) => {
      if (skip) return;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const mergedVariables = { ...variablesRef.current, ...queryVariables };
      const cacheKey = getCacheKey(query, mergedVariables);
      if (!bypassCache && fetchPolicy === "cache-and-network") {
        const cachedData = getFromCache(cacheKey);
        if (cachedData) {
          setData(cachedData);
        }
      }
      setLoading(true);
      setNetworkStatus(1);
      abortControllerRef.current = new AbortController();
      try {
        const graphqlRequest = {
          query,
          variables: mergedVariables
        };
        const response = await fetch("/api/plm_graphql", {
          method: "POST",
          headers: {
            ...getDefaultHeaders(),
            ...headersRef.current
          },
          body: JSON.stringify(graphqlRequest),
          signal: abortControllerRef.current.signal
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.errors && result.errors.length > 0) {
          const error2 = new Error(result.errors[0].message);
          setError(error2);
          if (result.data) {
            setData(result.data);
            setCache(cacheKey, result.data);
          }
        } else {
          setData(result.data);
          setError(void 0);
          if (fetchPolicy !== "no-cache") {
            setCache(cacheKey, result.data);
          }
        }
        setNetworkStatus(7);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);
        setNetworkStatus(8);
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    },
    [query, skip, fetchPolicy]
  );
  const refetch = useCallback2(
    async (refetchVariables) => {
      await executeQuery(refetchVariables, true);
    },
    [executeQuery]
  );
  useEffect2(() => {
    executeQuery();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [executeQuery, JSON.stringify(variables), JSON.stringify(headers)]);
  return {
    data,
    loading,
    error,
    refetch,
    networkStatus
  };
}
function useGraphQLMutation(mutation) {
  const [data, setData] = useState2(void 0);
  const [loading, setLoading] = useState2(false);
  const [error, setError] = useState2(void 0);
  const executeMutation = useCallback2(
    async (options) => {
      setLoading(true);
      setError(void 0);
      try {
        const graphqlRequest = {
          query: mutation,
          variables: options?.variables
        };
        const response = await fetch("/api/plm_graphql", {
          method: "POST",
          headers: {
            ...getDefaultHeaders(),
            ...options?.headers
          },
          body: JSON.stringify(graphqlRequest)
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.errors && result.errors.length > 0) {
          const error2 = new Error(result.errors[0].message);
          setError(error2);
          return void 0;
        }
        setData(result.data);
        setError(void 0);
        return result.data;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);
        return void 0;
      } finally {
        setLoading(false);
      }
    },
    [mutation]
  );
  return [executeMutation, { data, loading, error }];
}

// src/components/ReactRouterCanvasAnchor.tsx
import { Anchor } from "@design-systems/canvas-ui";
import { Link } from "react-router-dom";
import { jsx as jsx2 } from "react/jsx-runtime";
var ReactRouterCanvasAnchor = (props) => {
  return /* @__PURE__ */ jsx2(Anchor, { ...props, as: Link });
};
var ReactRouterCanvasAnchor_default = ReactRouterCanvasAnchor;

// src/utils/formHelpers.ts
var characterCount = (value, maxLength) => {
  if (typeof value !== "string" || value.length === 0) {
    return `${maxLength} characters max`;
  }
  if (value.length >= maxLength) {
    return "No characters remaining";
  }
  return `${maxLength - value.length} characters remaining`;
};

// src/utils/colorHelpers.ts
var colorList = [
  "default",
  "green",
  "blue",
  "purple",
  "pink",
  "red",
  "orange",
  "yellow"
];
var getColor = (key) => {
  const index = Array.from(`${key}`).reduce((total, num) => total + num.charCodeAt(0), 0) % colorList.length;
  return colorList[index];
};

// src/components/PageLayout.tsx
import styles from "./PageLayout.module-NJKDTDC4.module.css";
import { jsx as jsx3 } from "react/jsx-runtime";
var PageLayout = ({
  children,
  className,
  withPageHeader
}) => {
  const wrapperClassName = withPageHeader ? "hc8-pt-4x hc8-ph-4x hc8-mb-4x" : "hc8-pt-8x hc8-ph-4x hc8-mb-4x";
  return /* @__PURE__ */ jsx3("div", { className: className ? className : wrapperClassName, children: /* @__PURE__ */ jsx3("div", { className: styles.content, children }) });
};
var PageLayout_default = PageLayout;

// src/components/CardIcon.tsx
import Icon from "@design-systems/react-icons";
import styles2 from "./CardIcon.module-OXJOASIY.module.css";
import { jsx as jsx4 } from "react/jsx-runtime";
var CardIcon = ({
  className,
  ...rest
}) => {
  return /* @__PURE__ */ jsx4("div", { className: `${className} ${styles2.cardIcon}`, children: /* @__PURE__ */ jsx4(Icon, { ...rest }) });
};
var CardIcon_default = CardIcon;

// src/components/UnsavedChangesModal.tsx
import { Button, Grid, Modal } from "@design-systems/canvas-ui";
import { jsx as jsx5, jsxs } from "react/jsx-runtime";
var UnsavedChangesModal = ({ isOpen, onLeave, onStay }) => {
  return /* @__PURE__ */ jsx5(
    Modal,
    {
      headingText: "Leave without saving?",
      isOpen,
      onClose: onStay,
      size: "sm",
      footer: /* @__PURE__ */ jsxs(Grid.Container, { justify: "end", children: [
        /* @__PURE__ */ jsx5(Grid.Item, { children: /* @__PURE__ */ jsx5(Button, { variant: "secondary", onClick: onLeave, children: "Leave without saving" }) }),
        /* @__PURE__ */ jsx5(Grid.Item, { children: /* @__PURE__ */ jsx5(Button, { variant: "primary", onClick: onStay, children: "Keep editing" }) })
      ] }),
      children: /* @__PURE__ */ jsx5("p", { children: "Your changes will be lost if you leave this page. " })
    }
  );
};
var UnsavedChangesModal_default = UnsavedChangesModal;

// src/hooks/useUnsavedChangesBlocker.ts
import { useBlocker } from "react-router-dom";
var useUnsavedChangesBlocker = (isDirty) => {
  const blocker = useBlocker(isDirty);
  return {
    isOpen: blocker.state === "blocked",
    onLeave: () => blocker.state === "blocked" && blocker.proceed(),
    onStay: () => blocker.state === "blocked" && blocker.reset()
  };
};

// src/hooks/usePageLoadErrorToast.ts
import { useEffect as useEffect3, useRef as useRef2 } from "react";
import { useToastAlert } from "@design-systems/canvas-ui";
var usePageLoadErrorToast = ({
  error,
  toastConfig
}) => {
  const makeToast = useToastAlert();
  const hasShownErrorToastRef = useRef2(false);
  useEffect3(() => {
    if (error && !hasShownErrorToastRef.current) {
      hasShownErrorToastRef.current = true;
      makeToast(toastConfig);
    } else if (!error && hasShownErrorToastRef.current) {
      hasShownErrorToastRef.current = false;
    }
  }, [error, makeToast, toastConfig]);
};

// src/pagination/PaginationContext.tsx
import { createContext, useCallback as useCallback3, useMemo, useState as useState3 } from "react";
import { useSearchParams } from "react-router-dom";
import { jsx as jsx6 } from "react/jsx-runtime";
var PaginationContext = createContext({
  currentPage: 0,
  pageSize: 10,
  totalElements: 10,
  totalPages: 1,
  setCurrentPage: (page) => {
  },
  setPageSize: ({
    newPageSize
  }) => {
  },
  goToLastPage: () => {
  },
  goToFirstPage: () => {
  },
  goToNextPage: () => {
  },
  goToPreviousPage: () => {
  },
  itemRange: { start: 0, end: 0 },
  updateTotalElements: (totalElements) => {
  },
  updateTotalPages: (totalPages) => {
  }
});
var PaginationProvider = ({
  children,
  paginationPrefix,
  mode = "STATELESS"
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = useMemo(() => {
    const pageAsNumber = Number(
      searchParams.get(`${paginationPrefix ?? ""}page`) || "0"
    );
    return Number.isFinite(pageAsNumber) ? pageAsNumber : 0;
  }, [searchParams, paginationPrefix]);
  const pageSize = useMemo(() => {
    const pageSizeAsNumber = Number(
      searchParams.get(`${paginationPrefix ?? ""}pageSize`) || "10"
    );
    return Number.isFinite(pageSizeAsNumber) ? pageSizeAsNumber : 10;
  }, [searchParams, paginationPrefix]);
  const [statePage, setStatePage] = useState3(0);
  const [statePageSize, setStatePageSize] = useState3(10);
  const [totalElements, setTotalElements] = useState3(10);
  const [totalPages, setTotalPages] = useState3(1);
  const currentPage = useMemo(() => {
    return mode === "STATELESS" ? page : statePage;
  }, [mode, page, statePage]);
  const currentPageSize = useMemo(() => {
    return mode === "STATELESS" ? pageSize : statePageSize;
  }, [mode, pageSize, statePageSize]);
  const handlePageChange = useCallback3(
    (newPage) => {
      if (mode === "STATELESS") {
        setSearchParams({
          [`${paginationPrefix ?? ""}page`]: newPage.toString(),
          [`${paginationPrefix ?? ""}pageSize`]: pageSize.toString()
        });
      } else {
        setStatePage(newPage);
        setStatePageSize(pageSize);
      }
    },
    [mode, pageSize, paginationPrefix, setSearchParams]
  );
  const handlePageSizeChange = useCallback3(
    ({
      newPageSize
    }) => {
      if (mode === "STATELESS") {
        setSearchParams({
          [`${paginationPrefix ?? ""}page`]: "0",
          [`${paginationPrefix ?? ""}pageSize`]: newPageSize.toString()
        });
      } else {
        setStatePage(0);
        setStatePageSize(newPageSize);
      }
    },
    [mode, paginationPrefix, setSearchParams]
  );
  const handleLastPage = useCallback3(() => {
    handlePageChange(totalPages - 1);
  }, [handlePageChange, totalPages]);
  const handleFirstPage = useCallback3(() => {
    handlePageChange(0);
  }, [handlePageChange]);
  const handleNextPage = useCallback3(() => {
    handlePageChange(currentPage + 1);
  }, [handlePageChange, currentPage]);
  const handlePreviousPage = useCallback3(() => {
    handlePageChange(currentPage - 1);
  }, [handlePageChange, currentPage]);
  const itemRange = useMemo(() => {
    const start = currentPage * currentPageSize + 1;
    const end = Math.min(start + currentPageSize - 1, totalElements);
    return { start, end };
  }, [totalElements, currentPage, currentPageSize]);
  const providerValues = useMemo(
    () => ({
      currentPage,
      pageSize: currentPageSize,
      totalElements,
      totalPages,
      setCurrentPage: handlePageChange,
      setPageSize: handlePageSizeChange,
      goToLastPage: handleLastPage,
      goToFirstPage: handleFirstPage,
      goToNextPage: handleNextPage,
      goToPreviousPage: handlePreviousPage,
      itemRange,
      updateTotalElements: setTotalElements,
      updateTotalPages: setTotalPages
    }),
    [
      currentPage,
      currentPageSize,
      totalElements,
      totalPages,
      handlePageChange,
      handlePageSizeChange,
      handleLastPage,
      handleFirstPage,
      handleNextPage,
      handlePreviousPage,
      itemRange
    ]
  );
  return /* @__PURE__ */ jsx6(PaginationContext.Provider, { value: providerValues, children });
};

// src/pagination/hooks/useContextualizedPaging.ts
import { useContext } from "react";
var useContextualizedPaging = () => {
  const context = useContext(PaginationContext);
  if (!context) {
    throw new Error(
      "useContextualizedPaging must be used within a PaginationProvider"
    );
  }
  return context;
};

// src/pagination/hooks/useContextualizedUpdateTotals.ts
import { useEffect as useEffect4 } from "react";
var useContextualizedUpdateTotals = ({
  totalElements,
  totalPages
}) => {
  const { updateTotalElements, updateTotalPages } = useContextualizedPaging();
  useEffect4(() => {
    if (totalElements !== void 0 && totalPages !== void 0) {
      updateTotalElements(totalElements);
      updateTotalPages(totalPages);
    }
  }, [totalElements, totalPages, updateTotalElements, updateTotalPages]);
};

// src/pagination/ContextualizedPagination.tsx
import { useContext as useContext2 } from "react";
import { Pagination } from "@design-systems/canvas-ui";
import { jsx as jsx7 } from "react/jsx-runtime";
var ContextualizedPagination = ({
  ...canvasPaginationProps
}) => {
  const {
    currentPage,
    pageSize,
    totalElements,
    totalPages,
    setCurrentPage,
    setPageSize,
    goToLastPage,
    goToFirstPage,
    goToNextPage,
    goToPreviousPage,
    itemRange
  } = useContext2(PaginationContext);
  return /* @__PURE__ */ jsx7(
    Pagination,
    {
      currentPage: currentPage + 1,
      pageSize,
      totalItems: totalElements,
      totalPages,
      onRequestPage: setCurrentPage,
      onPageSizeChange: setPageSize,
      onRequestLast: goToLastPage,
      onRequestFirst: goToFirstPage,
      onRequestNext: goToNextPage,
      onRequestPrev: goToPreviousPage,
      itemRange,
      hasTotalItems: true,
      hasPageSize: true,
      pageSizeOptions: [10, 25, 50, 100],
      ...canvasPaginationProps
    }
  );
};
var ContextualizedPagination_default = ContextualizedPagination;

// src/components/ConfirmModal.tsx
import { Button as Button2, Grid as Grid2, Modal as Modal2 } from "@design-systems/canvas-ui";
import { jsx as jsx8, jsxs as jsxs2 } from "react/jsx-runtime";
var ConfirmModal = ({
  headingText,
  modalSize,
  cancelButtonText,
  confirmationButtonText,
  content,
  isOpen,
  onCancel,
  onConfirm
}) => {
  return /* @__PURE__ */ jsx8(
    Modal2,
    {
      headingText,
      isOpen,
      onClose: onCancel,
      size: modalSize,
      footer: /* @__PURE__ */ jsxs2(Grid2.Container, { justify: "end", children: [
        /* @__PURE__ */ jsx8(Grid2.Item, { children: /* @__PURE__ */ jsx8(Button2, { variant: "secondary", onClick: onCancel, children: cancelButtonText }) }),
        /* @__PURE__ */ jsx8(Grid2.Item, { children: /* @__PURE__ */ jsx8(Button2, { variant: "primary", onClick: onConfirm, children: confirmationButtonText }) })
      ] }),
      children: content
    }
  );
};
var ConfirmModal_default = ConfirmModal;
export {
  CardIcon_default as CardIcon,
  ConfirmModal_default as ConfirmModal,
  ContextualizedPagination_default as ContextualizedPagination,
  IsolatedComponent_default as IsolatedComponent,
  PageLayout_default as PageLayout,
  PaginationProvider,
  ReactRouterCanvasAnchor_default as ReactRouterCanvasAnchor,
  UnsavedChangesModal_default as UnsavedChangesModal,
  characterCount,
  getColor,
  useContextualizedPaging,
  useContextualizedUpdateTotals,
  useGraphQL,
  useGraphQLMutation,
  usePageLoadErrorToast,
  useResponsiveLayout_default as useResponsiveLayout,
  useUnsavedChangesBlocker
};
